// frontend/src/router.tsx
import getRouterBasename from '@/lib/router';
import { isAdminConsole, isAdminConsolePath, isDesktop } from '@/AppMode';
import { staffAccess } from '@/components/admin/sections';
import { useAuth } from '@chainlit/react-client';
import { useEffect } from 'react';
import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router-dom';

function ScrollToTopLayout() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  // Console master : ni chat, ni pages publiques — tout renvoie au back-office
  if (isAdminConsole() && !isAdminConsolePath(pathname)) {
    return <Navigate replace to="/admin" />;
  }
  // Viewer (lecture seule) : uniquement les sections qui lui sont ouvertes
  if (
    user &&
    pathname.startsWith('/admin') &&
    !staffAccess(user.metadata).canOpen(pathname)
  ) {
    return <Navigate replace to="/admin" />;
  }
  return <Outlet />;
}

import AuthCallback from 'pages/AuthCallback';
import Element from 'pages/Element';
import Env from 'pages/Env';
import Home from 'pages/Home';
import Login from 'pages/Login';
import Thread from 'pages/Thread';

// Import des pages admin (MASTER)
import Admin from 'pages/Admin';
import AdminHome from 'pages/Admin/Home';
import LegalFlow from 'pages/Admin/LegalFlow';
import Fiches from 'pages/Admin/Fiches';
import CreateTenant from 'pages/Admin/CreateTenant';
import AdminUsers from 'pages/Admin/Users';
import AdminAgents from 'pages/Admin/Agents';
import AdminAgentDetail from 'pages/Admin/AgentDetail';
import AdminTools from 'pages/Admin/Tools';
import AdminMCPServers from 'pages/Admin/MCPServers';
import ChangePassword from 'pages/ChangePassword';
import ForgotPassword from 'pages/ForgotPassword';
import ResetPassword from 'pages/ResetPassword';
import Signup from 'pages/Signup';
import APIKeys from './pages/Admin/APIKeys';
import BroadcastEmail from './pages/Admin/BroadcastEmail';
import RGPDValidation from './pages/Admin/RGPDValidation';
import ProspectDashboard from './pages/Admin/ProspectDashboard';
import EmailQueue from './pages/Admin/EmailQueue';
import Annuaire from './pages/Admin/Annuaire';
import LinkedInPosts from './pages/Admin/LinkedInPosts';

// Import des pages users (TENANT)
import TenantUsers from 'pages/Users';
import TenantDetail from 'pages/TenantDetails';
import EditTenant from 'pages/Admin/EditTenant';
import Profile from 'pages/Profile';

// Import des pages publiques
import PublicJurisprudence from './pages/Public/Jurisprudence';
import PublicFiche from './pages/Public/Fiche';
import PublicBrowse from './pages/Public/PublicBrowse';
import PublicArticle from './pages/Public/PublicArticle';
import PublicLogin from './pages/Public/PublicLogin';
import PublicPricing from './pages/Public/PublicPricing';
import PublicLegalGraph from './pages/Public/PublicLegalGraph';
import PublicContact from './pages/Public/PublicContact';
import PublicSources from './pages/Public/PublicSources';
import PublicPrincipes from './pages/Public/PublicPrincipes';
import PublicRGPD      from './pages/Public/PublicRGPD';
import PublicCJUE      from './pages/Public/PublicCJUE';
import PublicCJUEFiche from './pages/Public/PublicCJUEFiche';
import PublicArticles  from './pages/Public/PublicArticles';
import PublicReglements       from './pages/Public/PublicReglements';
import PublicReglementArticle from './pages/Public/PublicReglementArticle';
import PublicCnil      from './pages/Public/Cnil/index';
import CnilFiche       from './pages/Public/Cnil/CnilFiche';
import PublicConstit from './pages/Public/Constit';
import PublicDesktop from './pages/Public/PublicDesktop';
import DesktopHome from './pages/Public/DesktopHome';
import ConstitDecisionPage from './pages/Public/Constit/ConstitDecision';
import TrialsHome      from './pages/Public/Trials/index';
import TrialRoom       from './pages/Public/Trials/TrialRoom';
import { MARKET_HOME, getPreferredMarket, isBot } from '@/lib/market';
import { MentionsLegales }   from '@/components/jurisprudence/MentionsLegales';
import { PolitiqueCookies }  from '@/components/jurisprudence/PolitiqueCookies';
import { QuiSommesNous }     from '@/components/jurisprudence/QuiSommesNous';


// Import des pages Settings (remplace l'ancienne Settings.tsx)
import SettingsProfile      from '@/components/jurisprudence/SettingsProfile';
import SettingsSubscription from '@/components/jurisprudence/SettingsSubscription';
import SettingsApiKey       from '@/components/jurisprudence/SettingsApiKey';
import SettingsInvoices from '@/components/jurisprudence/SettingsInvoices';
import Formations from '@/components/jurisprudence/Formations';


// En mode legalgraph, / est l'accueil de la juridiction de l'utilisateur :
// droit français ici, sinon redirection vers /jurisprudence/cjue
// (jamais pour les robots : / reste l'accueil FR indexé).
// En mode legalchat,  / affiche Home (chat Chainlit) sur les tenants ;
// la console master (legalchat.smartlawyer.ai) est redirigée vers /admin par ScrollToTopLayout.
function RootPage() {
  // useLocation : se ré-afficher quand DesktopHome navigue vers /?q=… (même route,
  // sinon React réutilise l'élément et la recherche ne s'ouvre jamais)
  const location = useLocation();
  // App desktop : accueil produit (recherche + agents) ; /?q=… reste la recherche
  if (isDesktop() && !new URLSearchParams(location.search).get('q')) return <DesktopHome />;
  if (window.__APP_MODE__ !== 'legalgraph') return <Home />;
  const market = getPreferredMarket();
  const params = new URLSearchParams(window.location.search);
  params.delete('lang');
  params.delete('market');
  if (market !== 'fr' && !isBot() && [...params.keys()].length === 0) {
    return <Navigate replace to={`${MARKET_HOME[market]}${window.location.search}`} />;
  }
  return <PublicJurisprudence />;
}

export const router = createBrowserRouter(
  [
    {
      element: <ScrollToTopLayout />,
      children: [
    // ========================================
    // Route racine
    // ========================================
    {
      path: '/',
      element: <RootPage />,
    },

    // ========================================
    // Routes PUBLIQUES — Jurisprudence
    // ========================================
    {
      path: '/jurisprudence',
      element: <Navigate replace to="/" />,
    },
    {
      path: '/browse',
      element: <PublicBrowse />,
    },
    {
      path: '/jurisprudence/:jur/:ville/:chambre/:date/:numero',
      element: <PublicFiche />,
    },
    {
      path: '/jurisprudence/:jur/:chambre/:date/:numero',
      element: <PublicFiche />,
    },
    {
      path: '/jurisprudence/principes',
      element: <PublicPrincipes />,
    },
    {
      path: '/jurisprudence/rgpd',
      element: <PublicRGPD />,
    },
    {
      path: '/jurisprudence/cjue',
      element: <PublicCJUE />,
    },
    {
      path: '/jurisprudence/cjue/:celex',
      element: <PublicCJUEFiche />,
    },
    {
      path: '/cnil/deliberations',
      element: <PublicCnil />,
    },
    {
      path: '/desktop',
      element: <PublicDesktop />,
    },
    {
      path: '/conseil-constitutionnel',
      element: <PublicConstit />,
    },
    {
      path: '/conseil-constitutionnel/:numero',
      element: <ConstitDecisionPage />,
    },
    {
      path: '/cnil/deliberations/:num',
      element: <CnilFiche />,
    },
    // ========================================
    // Juridiction US (fiches CourtListener, EN)
    // ========================================
    {
      path: '/tribunal',
      element: <TrialsHome />,
    },
    {
      path: '/tribunal/:trialId',
      element: <TrialRoom />,
    },
    {
      path: '/articles',
      element: <PublicArticles />,
    },
    {
      path: '/reglements',
      element: <PublicReglements />,
    },
    {
      path: '/reglements/:slugCode/:numero',
      element: <PublicReglementArticle />,
    },
    {
      path: '/articles/by-node-id/:nodeId',
      element: <PublicArticle />,
    },
    {
      path: '/articles/:slugCode/:numero',
      element: <PublicArticle />,
    },
    {
      path: '/articles/article-lookup',
      element: <PublicArticle />,
    },
    {
      path: '/legal-graph',
      element: <PublicLegalGraph />,
    },
    { path: '/contact',            element: <PublicContact /> },
    { path: '/sources',            element: <PublicSources /> },
    { path: '/mentions-legales',   element: <MentionsLegales /> },
    { path: '/politique-cookies',  element: <PolitiqueCookies /> },
    { path: '/qui-sommes-nous',    element: <QuiSommesNous /> },
    { path: '/formations', element: <Formations /> },


    // ========================================
    // Routes Settings — profil / abonnement / clé API
    // ========================================
    {
      path: '/settings',
      element: <SettingsProfile />,
    },
    {
      path: '/settings/subscription',
      element: <SettingsSubscription />,
    },
    {
      path: '/settings/api-key',
      element: <SettingsApiKey />,
    },
    { path: '/settings/invoices', element: <SettingsInvoices /> },


    // ========================================
    // Routes AUTH
    // ========================================
    {
      path: '/env',
      element: <Env />,
    },
    {
      path: '/thread/:id?',
      element: <Thread />,
    },
    {
      path: '/element/:id',
      element: <Element />,
    },
    {
      path: '/login',
      element: window.__APP_MODE__ === 'legalgraph' ? <PublicLogin /> : <Login />,
    },
    { path: '/pricing', element: <PublicPricing /> },
    {
      path: '/login/callback',
      element: <AuthCallback />,
    },
    {
      path: '/share/:id',
      element: <Thread />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPassword />,
    },
    {
      path: '/reset-password',
      element: <ResetPassword />,
    },
    {
      path: '/signup',
      element: <Signup />,
    },
    {
      path: '/change-password',
      element: <ChangePassword />,
    },

    // ========================================
    // Routes ADMIN MASTER
    // ========================================
    {
      path: '/admin',
      element: <AdminHome />,
    },
    {
      path: '/admin/tenants',
      element: <Admin />,
    },
    {
      path: '/admin/legalflow',
      element: <LegalFlow />,
    },
    {
      path: '/admin/tenants/create',
      element: <CreateTenant />,
    },
    {
      path: '/admin/tenants/:tenantId',
      element: <TenantDetail />,
    },
    {
      path: '/admin/tenants/:tenantId/edit',
      element: <EditTenant />,
    },
    {
      path: '/admin/api-keys',
      element: <APIKeys />,
    },
    {
      path: '/admin/users',
      element: <AdminUsers />,
    },
    {
      path: '/admin/agents',
      element: <AdminAgents />,
    },
    {
      path: '/admin/agents/:agentId',
      element: <AdminAgentDetail />,
    },
    {
      path: '/admin/tools',
      element: <AdminTools />,
    },
    {
      path: '/admin/mcp-servers',
      element: <AdminMCPServers />,
    },
    {
      path: '/admin/fiches',
      element: <Fiches />,
    },
    {
      path: '/admin/broadcast',
      element: <BroadcastEmail />,
    },
    {
      path: '/admin/rgpd-validation',
      element: <RGPDValidation />,
    },
    {
      path: '/admin/prospects',
      element: <ProspectDashboard />,
    },
    {
      path: '/admin/email-queue',
      element: <EmailQueue />,
    },
    {
      path: '/admin/annuaire',
      element: <Annuaire />,
    },
    {
      path: '/admin/linkedin',
      element: <LinkedInPosts />,
    },

    // ========================================
    // Routes TENANT
    // ========================================
    {
      path: '/users',
      element: <TenantUsers />,
    },

    // ========================================
    // Routes communes (MASTER + TENANT)
    // ========================================
    {
      path: '/profile',
      element: <Profile />,
    },

    {
      path: '*',
      element: <Navigate replace to="/" />,
    },
      ],
    },
  ],
  { basename: getRouterBasename() }
);